import { ksaStartOfWeek } from "./ksaTime";
// Analytics & data transformation helpers — pure marketing/operations math only.
// Legacy "59 program" fields removed in 2026-02 per user requirement.
//
// v200.51 (2026-08-14) — Front-side COGS/FIX constants REMOVED (owner rule).
//   * `export const COGS = 0.611` deleted. The ratio must be derived
//     per period from product_costs × pos_sales quantities. The only
//     legitimate source is now `GET /api/analytics/cogs-window`.
//   * `export const FIX = 47500` deleted. No data source exists in the
//     database. The value is now labelled «غير موثّق» server-side (see
//     `diagnostics/cogs_estimate.py::_build_public_window_router` →
//     `fixed_costs.status = "undocumented"`), and consumers may show it
//     only with an explicit "unsourced" tag.
//   Guarded by `backend/tests/test_cogs_dashboard_wiring.py`.

// Hourly seed data (for shifts chart fallback). Only revenue + count + shift code.
export const HD = {
  0:  { rev: 621,  cnt: 168, sh: "S3" },
  1:  { rev: 437,  cnt: 98,  sh: "S3" },
  2:  { rev: 521,  cnt: 149, sh: "S3" },
  3:  { rev: 1265, cnt: 376, sh: "S3" },
  4:  { rev: 1754, cnt: 446, sh: "S3" },
  5:  { rev: 1829, cnt: 288, sh: "S3" },
  6:  { rev: 1445, cnt: 274, sh: "S3" },
  7:  { rev: 1286, cnt: 232, sh: "S1" },
  8:  { rev: 1409, cnt: 272, sh: "S1" },
  9:  { rev: 1761, cnt: 291, sh: "S1" },
  10: { rev: 1935, cnt: 362, sh: "S1" },
  11: { rev: 1984, cnt: 357, sh: "S1" },
  12: { rev: 2280, cnt: 392, sh: "S1" },
  13: { rev: 2722, cnt: 524, sh: "S1" },
  14: { rev: 3995, cnt: 648, sh: "S1" },
  15: { rev: 3343, cnt: 593, sh: "S2" },
  16: { rev: 3704, cnt: 615, sh: "S2" },
  17: { rev: 3313, cnt: 551, sh: "S2" },
  18: { rev: 2932, cnt: 542, sh: "S2" },
  19: { rev: 2555, cnt: 471, sh: "S2" },
  20: { rev: 2123, cnt: 495, sh: "S2" },
  21: { rev: 1774, cnt: 366, sh: "S2" },
  22: { rev: 1501, cnt: 297, sh: "S2" },
  23: { rev: 946,  cnt: 255, sh: "S3" },
};

export const fm = (n) => Math.round(n).toLocaleString("en-US");
export const av = (a) => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0);

export function parseDate(dr) {
  if (dr instanceof Date) return { d: dr.toISOString().slice(0, 10), r: dr.toISOString() };
  const s = String(dr || "");
  if (s.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/)) return { d: s.slice(0, 10), r: s };
  if (s.match(/\d{4}-\d{2}-\d{2}/)) return { d: s.slice(0, 10), r: s };
  if (s.includes("/")) {
    const p = s.split(/[/ ]/);
    if (p.length >= 3) {
      return {
        d: `${(p[2] || "").slice(0, 4)}-${String(p[1]).padStart(2, "0")}-${String(p[0]).padStart(2, "0")}`,
        r: s,
      };
    }
  }
  const n = Number(s);
  if (n > 40000 && n < 50000) {
    const dt = new Date((n - 25569) * 86400 * 1000);
    return { d: dt.toISOString().slice(0, 10), r: dt.toISOString() };
  }
  return { d: "", r: s };
}

export function getHour(raw) {
  const m = String(raw || "").match(/(\d{2}):\d{2}/);
  return m ? parseInt(m[1], 10) : 12;
}

// D1 — حدود الفترات من الإعدادات (/pos/dayparts/settings) لا من الكود؛ S1/S2/S3 = ترتيب الفترات فيها.
let _dayparts = [];
export function setDayparts(parts) { _dayparts = Array.isArray(parts) ? parts : []; }
export function getShift(h) {
  const t = `${String(h).padStart(2, "0")}:00`;
  const i = _dayparts.findIndex((p) => (p.start < p.end ? t >= p.start && t < p.end : t >= p.start || t < p.end));
  return `S${(i < 0 ? 0 : i) + 1}`;
}

export function byDay(rows) {
  const m = {};
  rows.forEach((r) => {
    if (!m[r.date]) {
      m[r.date] = { date: r.date, total: 0, disc: 0, count: 0, paid: 0, free: 0 };
    }
    const d = m[r.date];
    d.total += r.total;
    d.disc += r.disc;
    d.count++;
    if (r.isPaid) d.paid++;
    if (r.isFree) d.free++;
  });
  return Object.values(m).sort((a, b) => (a.date > b.date ? 1 : -1));
}

export function byWeek(days) {
  const m = {};
  days.forEach((d) => {
    const wk = ksaStartOfWeek(String(d.date).slice(0, 10), "monday");
    if (!m[wk]) m[wk] = { week: wk, total: 0, disc: 0, count: 0, paid: 0, free: 0, days: 0 };
    const w = m[wk];
    w.total += d.total;
    w.disc += d.disc;
    w.count += d.count;
    w.paid += (d.paid || 0);
    w.free += (d.free || 0);
    w.days++;
  });
  return Object.values(m).sort((a, b) => (a.week > b.week ? 1 : -1));
}

// Marketing aggregator: bucket rows by month
export function byMonth(rows) {
  const m = {};
  rows.forEach((r) => {
    const key = (r.date || "").slice(0, 7);
    if (!key) return;
    if (!m[key]) m[key] = { month: key, total: 0, disc: 0, count: 0, paid: 0, free: 0 };
    m[key].total += r.total || 0;
    m[key].disc  += r.disc  || 0;
    m[key].count += 1;
    if (r.isPaid) m[key].paid += 1; else m[key].free += 1;
  });
  return Object.values(m).sort((a, b) => a.month.localeCompare(b.month));
}
